import {
  ChevronsUpDown,
  LogOut,
  Check,
  Building2,
  Moon,
  Sun,
  Monitor,
  CirclePlay,
} from 'lucide-react';
import { useUser, useClerk } from '@clerk/clerk-react';
import { useNavigate } from '@tanstack/react-router';
import { useMemo } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@loader/ui';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@loader/ui';
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@loader/ui';

import { useWorkspaceStore } from '@/store/useWorkspaceStore';
import { useRpc } from '@/lib/rpc';
import { useTheme } from '@/hooks/useTheme';
import { useWorkspaceMembership, useWorkspaces } from '@/hooks';
import { clearWorkspaceScopedStores } from '@/utils/storeCleanup';
import { sortWorkspacesByName } from '@/utils/workspaceSorting';
import { capitalize } from '@/utils/formatters';
import { useOnboardingStore } from '@/features/onboarding';

export function NavUser() {
  const { isMobile } = useSidebar();
  const { user } = useUser();
  const { signOut, openUserProfile } = useClerk();
  const navigate = useNavigate();
  const { selectedWorkspaceId, setSelectedWorkspaceId, clearSelectedWorkspace } = useWorkspaceStore();
  const { theme, setTheme } = useTheme();
  const rpc = useRpc();
  const openOnboarding = useOnboardingStore((s) => s.open);

  // Fetch current workspace info using TanStack Query
  const { data: membershipData } = useWorkspaceMembership();
  const workspaceName = membershipData?.workspace?.name ?? null;
  const workspaceRole = membershipData?.membership?.role ?? null;

  // Fetch all workspaces using TanStack Query
  const { workspaces: allWorkspaces, isLoading: loadingWorkspaces } = useWorkspaces();

  const sortedWorkspaces = useMemo(() => {
    return sortWorkspacesByName(allWorkspaces);
  }, [allWorkspaces]);

  // Handle workspace switch
  const handleWorkspaceSwitch = async (workspaceId: string) => {
    if (workspaceId === selectedWorkspaceId) {
      return; // Already selected
    }

    try {
      // Persist selection to database (required for auth middleware)
      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.workspaces.select.$post({
        json: { workspace_id: workspaceId },
      });

      if (!res.ok) {
        throw new Error('Failed to save workspace selection');
      }

      // Update local store
      setSelectedWorkspaceId(workspaceId);
      // Navigate to workspace root to refresh context with new workspace
      window.location.href = '/workspace';
    } catch (error) {
      console.error('Failed to switch workspace:', error);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      // Clear after signOut to avoid flickering (isSignedIn is now false)
      clearSelectedWorkspace();
      // Clear all workspace-scoped stores to prevent data leakage
      clearWorkspaceScopedStores();
      void navigate({ to: '/login' });
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  if (!user) {
    return null;
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <Avatar className="h-8 w-8 rounded-lg">
                <AvatarImage src={user.imageUrl} alt={user.fullName ?? ''} />
                <AvatarFallback className="rounded-lg">
                  {user.firstName?.charAt(0) ?? 'U'}
                  {user.lastName?.charAt(0) ?? ''}
                </AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-semibold">
                  {user.fullName ?? 'User'}
                </span>
                <span className="text-muted-foreground truncate text-xs">
                  {user.primaryEmailAddress?.emailAddress}
                </span>
              </div>
              <ChevronsUpDown className="ml-auto h-4 w-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
            side={isMobile ? 'bottom' : 'right'}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuItem
              onClick={() => openUserProfile()}
              className="cursor-pointer"
            >
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <Avatar className="h-8 w-8 rounded-lg">
                  <AvatarImage src={user.imageUrl} alt={user.fullName ?? ''} />
                  <AvatarFallback className="rounded-lg">
                    {user.firstName?.charAt(0) ?? 'U'}
                    {user.lastName?.charAt(0) ?? ''}
                  </AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-medium">
                    {workspaceName ?? 'Workspace'}
                  </span>
                  <span className="truncate text-xs">
                    {workspaceRole ? capitalize(workspaceRole) : user.primaryEmailAddress?.emailAddress}
                  </span>
                </div>
              </div>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              {loadingWorkspaces ? (
                <DropdownMenuItem disabled>
                  <span className="text-xs text-muted-foreground">Loading workspaces...</span>
              </DropdownMenuItem>
              ) : sortedWorkspaces.length > 0 ? (
                sortedWorkspaces.map((workspace) => {
                  const isCurrent = workspace.id === selectedWorkspaceId;
                  return (
                    <DropdownMenuItem
                      key={workspace.id}
                      onClick={() => handleWorkspaceSwitch(workspace.id)}
                      className={isCurrent ? 'bg-accent' : ''}
                    >
                      <Building2 className="mr-2 h-4 w-4" />
                      <span className="flex-1">{workspace.name}</span>
                      {isCurrent && <Check className="h-4 w-4" />}
              </DropdownMenuItem>
                  );
                })
              ) : (
                <DropdownMenuItem disabled>
                  <span className="text-xs text-muted-foreground">No workspaces available</span>
              </DropdownMenuItem>
              )}
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuLabel className="text-xs text-muted-foreground">
                Theme
              </DropdownMenuLabel>
              <DropdownMenuItem
                onClick={() => setTheme('light')}
                className={theme === 'light' ? 'bg-accent' : ''}
              >
                <Sun className="mr-2 h-4 w-4" />
                <span className="flex-1">Light</span>
                {theme === 'light' && <Check className="h-4 w-4" />}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setTheme('dark')}
                className={theme === 'dark' ? 'bg-accent' : ''}
              >
                <Moon className="mr-2 h-4 w-4" />
                <span className="flex-1">Dark</span>
                {theme === 'dark' && <Check className="h-4 w-4" />}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setTheme('system')}
                className={theme === 'system' ? 'bg-accent' : ''}
              >
                <Monitor className="mr-2 h-4 w-4" />
                <span className="flex-1">System</span>
                {theme === 'system' && <Check className="h-4 w-4" />}
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={openOnboarding}>
              <CirclePlay className="mr-2 h-4 w-4" />
              Replay Tour
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleSignOut}>
              <LogOut />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
